import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { TrashIcon, ComputerDesktopIcon, DevicePhoneMobileIcon } from '@heroicons/react/24/outline';
import { Card, CardContent } from './common/Card';
import { Button } from './common/Button';
import { useNotification } from '../contexts/NotificationContext';
import QRCodeGenerator from './QRCodeGenerator';
import mobileDeviceService from '../services/mobileDeviceService';

interface DeviceProps {
  id: number;
  device_name: string;
  device_model?: string;
  os_type?: string;
  last_login: string;
  created_at: string;
}

interface DeviceManagementProps {
  userId: number | string;
  username: string;
}

const DeviceManagement: React.FC<DeviceManagementProps> = ({
  userId,
  username
}) => {
  const { showNotification } = useNotification();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);

  // Fetch user's paired devices
  const { data: devices, isLoading: isLoadingDevices } = useQuery({
    queryKey: ['userDevices'],
    queryFn: mobileDeviceService.getUserDevices,
  });

  // Delete device mutation
  const deleteDeviceMutation = useMutation({
    mutationFn: mobileDeviceService.deleteDevice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userDevices'] });
      showNotification({
        type: 'success',
        title: 'Device Removed',
        message: 'The device has been successfully unpaired'
      });
    },
    onError: (error: any) => {
      console.error('Delete device error:', error);
      const errorMessage = error?.response?.data?.error || 
                          error?.response?.data?.message || 
                          error?.message || 
                          'Failed to remove device';
      showNotification({
        type: 'error',
        title: 'Error',
        message: errorMessage
      });
    }
  });

  // Generate pairing token mutation
  const generateTokenMutation = useMutation({
    mutationFn: mobileDeviceService.generatePairingToken,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['userDevices'] });
    },
    onError: (error: any) => {
      showNotification({
        type: 'error',
        title: 'Error',
        message: error?.response?.data?.error || 'Failed to generate pairing token'
      });
    }
  });

  // Handle device deletion
  const handleDeleteDevice = (deviceId: number, deviceName: string) => {
    if (window.confirm(`Are you sure you want to remove the device "${deviceName}"? This device will no longer be able to access your account.`)) {
      deleteDeviceMutation.mutate(deviceId);
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('default', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Helper to get device icon
  const getDeviceIcon = (osType?: string) => {
    if (!osType) return <DevicePhoneMobileIcon className="h-5 w-5" />;
    
    const os = osType.toLowerCase();
    if (os.includes('ios') || os.includes('android')) {
      return <DevicePhoneMobileIcon className="h-5 w-5" />;
    }
    return <ComputerDesktopIcon className="h-5 w-5" />;
  };

  return (
    <Card className="overflow-hidden">
      <div className="bg-gray-50 px-4 py-2 border-b">
        <h2 className="font-medium text-gray-700">Mobile Devices</h2>
      </div>
      <CardContent className="p-4">
        <div className="mb-4">
          <h3 className="text-base font-medium text-gray-900 mb-2">Paired Devices</h3>
          <p className="text-sm text-gray-500 mb-4">
            These are the devices currently paired with your account. You can remove devices at any time.
          </p>

          {isLoadingDevices && (
            <div className="text-center py-4">
              <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
              <p className="mt-2 text-sm text-gray-500">Loading devices...</p>
            </div>
          )}

          {devices && devices.length === 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded py-3 px-4 text-gray-600 text-center">
              No devices are currently paired with your account.
            </div>
          )}

          {devices && devices.length > 0 && (
            <div className="overflow-hidden border border-gray-200 rounded">
              <ul className="divide-y divide-gray-200">
                {devices.map((device: DeviceProps) => (
                  <li key={device.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center">
                      <div className="mr-3 flex-shrink-0 text-gray-400">
                        {getDeviceIcon(device.os_type)}
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{device.device_name}</h4>
                        <div className="text-sm text-gray-500">
                          {device.device_model && (
                            <span className="block">{device.device_model}</span>
                          )}
                          <span className="block">Last active: {formatDate(device.last_login)}</span>
                          <span className="block">Paired: {formatDate(device.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteDevice(device.id, device.device_name)}
                      className="p-1 text-gray-400 hover:text-red-500"
                      title="Remove device"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="mt-6 border-t border-gray-200 pt-6">
          <h3 className="text-base font-medium text-gray-900 mb-2">Pair New Device</h3>
          <p className="text-sm text-gray-500 mb-4">
            Generate a QR code to connect a new mobile device to your account. 
            The QR code expires after 1 hour for security reasons.
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="mb-4 sm:mb-0">
              <QRCodeGenerator 
                userId={userId}
                username={username}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DeviceManagement;